import { useDeepCompareEffect } from "@react-hookz/web";
import { useAtomValue } from "jotai";
import { useCallback, useEffect, useState } from "react";

import { config } from "shared/data";

import type { Props } from "scmp-app/components/advertisement/ad-slots/ad-slot";
import * as aps from "scmp-app/components/advertisement/amazon-publisher-service/hooks";
import { magnitePrebidJSAtom } from "scmp-app/components/advertisement/magnite-demand-manager/atoms";
import * as md from "scmp-app/components/advertisement/magnite-demand-manager/hooks";

export const useAdSlotWithRefresh = (props: Exclude<Props, "manualRefreshCount">) => {
  const [manualRefreshCount, setManualRefreshCount] = useState(0);

  useDeepCompareEffect(() => {
    setManualRefreshCount(0);
  }, [props]);

  return {
    props: {
      ...props,
      manualRefreshCount,
    },
    refresh: useCallback(() => setManualRefreshCount(v => v + 1), [setManualRefreshCount]),
  };
};

export const useRenderSlotsWithBids = (slot: googletag.Slot | null, refreshCount?: number) => {
  const { handleFetchBids: handleFetchApsBids } = aps.useFetchBids(slot);
  const { handleFetchBids: handleFetchMdBids } = md.useFetchBids(slot);
  const { isReady: isMagnitePrebidJSReady } = useAtomValue(magnitePrebidJSAtom);
  const { isAllow: isApsAllowed } = aps.useCheckGeo();
  const { shouldSkipMagnite } = md.useCheckGeo();

  useEffect(() => {
    if (!slot) return;

    const getFailsafeTimeoutPromise = () =>
      new Promise<void>(resolve => {
        setTimeout(resolve, config.advertisement.failsafeTimeout);
      });

    let hasCancel = false;

    const run = async () => {
      const promises = [];

      // Wait for and execute Magnite bidding if Magnite is not skipped
      if (!shouldSkipMagnite) {
        // Wait for Magnite script ready
        if (!isMagnitePrebidJSReady) return;
        promises.push(handleFetchMdBids());
      }

      // Add APS if country is allowed
      if (isApsAllowed) {
        promises.push(handleFetchApsBids());
      }

      // Wait for all bids (or skip if both are skipped)
      if (promises.length > 0) {
        await Promise.race([Promise.all(promises), getFailsafeTimeoutPromise()]);
      }

      // Refresh GAM with targeting keys from header bidding
      if (!hasCancel) {
        googletag.cmd.push(() => {
          googletag.pubads().refresh([slot]);
        });
      }
    };
    void run();

    return () => {
      hasCancel = true;
    };
  }, [
    slot,
    refreshCount,
    handleFetchApsBids,
    handleFetchMdBids,
    isApsAllowed,
    isMagnitePrebidJSReady,
    shouldSkipMagnite,
  ]);
};
